import React, { useEffect, useCallback } from 'react';
import { debug } from '@/utils/debug';
import { formatAccountId } from '@/utils/googleAds';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import styles from './EmailConfirmation.module.css';
import modalStyles from '@/styles/ModalStyles.module.css';

interface EmailConfirmationProps {
  customerId: string;
  isPolling: boolean;
  pollingAttempts: number;
  pollingPhase: 'fast' | 'slow';
  pollingStatus: string;
  isRefreshingData: boolean;
  onGoHome: () => void;
}

export function EmailConfirmation({
  customerId,
  isPolling,
  pollingAttempts,
  pollingPhase,
  pollingStatus,
  isRefreshingData,
  onGoHome,
}: EmailConfirmationProps) {
  debug.log('EmailConfirmation: Rendering with polling status', {
    isPolling,
    pollingAttempts,
    pollingPhase,
    pollingStatus
  });

  // Check if we're in the setup/refresh phase - use explicit state instead of parsing messages
  const isSettingUp = isRefreshingData;

  // Handle Escape key to close modal
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onGoHome();
    }
  }, [onGoHome]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const modalTitleId = 'email-confirmation-title';

  return (
    <div
      className={modalStyles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalTitleId}
    >
      <div className={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={modalStyles.modalHeader}>
          <h3 id={modalTitleId} className={modalStyles.modalTitle}>
            {isSettingUp ? 'Setting Up Your Account' : 'Check Your Email'}
          </h3>
          <button
            className={modalStyles.modalClose}
            onClick={onGoHome}
            aria-label="Close dialog"
          >
            &times;
          </button>
        </div>

        <div className={modalStyles.modalBody}>
          <div className={styles.container}>
            {!isSettingUp && (
              <div className={styles.header}>
                <div className={styles.icon}>📧</div>
              </div>
            )}

            <div className={styles.content}>
              <div className={styles.message}>
                {!isSettingUp && (
                  <>
                    <p className={styles.primaryText}>
                      We've sent an invitation to link your Google Ads account.
                    </p>
                    <p className={styles.secondaryText}>
                      <strong>Customer ID:</strong> {formatAccountId(customerId)}
                    </p>
                  </>
                )}
                {!isSettingUp && (
                  <>
                    <p className={styles.instructions}>
                      Please check your email and follow these steps:
                    </p>
                    <ol className={styles.stepsList}>
                      <li><strong>Step 1)</strong> Look for an email from Google Ads. The email will be sent to the address that manages the Google Ads account.</li>
                      <li><strong>Step 2)</strong> Click <strong>"ACCEPT REQUEST"</strong> in the email</li>
                      <li><strong>Step 3)</strong> Complete the authorization in Google Ads console:
                        <ul>
                          <li>You should be directed to the <strong>Managers</strong> tab in <strong>Access and security</strong></li>
                          <li>Press "Accept" under <strong>Actions</strong> in the <strong>Link request</strong> section</li>
                        </ul>
                      </li>
                    </ol>
                  </>
                )}
              </div>

              {(isPolling || isRefreshingData) && (
                <div className={styles.pollingSection}>
                  <div className={styles.pollingHeader}>
                    <LoadingSpinner size="md" variant="primary" label={pollingStatus} />
                    <span className={styles.pollingText}>{pollingStatus}</span>
                  </div>
                </div>
              )}

              {!isSettingUp && (
                <div className={styles.help}>
                  <p className={styles.helpText}>
                    <strong>Having trouble?</strong> Make sure you're using the correct Customer ID and that you have
                    admin access to the Google Ads account. The invitation may take a few minutes to arrive.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}